import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { OutlineElement } from 'src/components/base/outline-element/outline-element';
import componentStyles from './idb-search-results-card.css.lit';
import { LinkTargetType } from 'src/components/base/outline-link/config';
import { getUrlTarget } from '../../../utilities/url_utils';

export type AllowedColors =
  | 'gray'
  | 'light-blue'
  | 'blue'
  | 'green'
  | 'white'
  | 'transparent';
export type ImageTypes = 'image' | 'video-thumbnail' | 'profile' | null;

/**
 * The IDB Search Results Card component.
 * @element idb-search-results-card
 * @extends OutlineElement
 * @see Course and Events Card for similar styling
 * @slot image-tags - The image-tags slot.
 * @slot image - The image slot.
 * @slot caption - The caption slot.
 * @slot eyebrow - The eyebrow slot.
 * @slot title - The title slot.
 * @slot summary - The summary slot.
 * @slot content - The primary content slot.
 * @slot cta - The CTA slot.
 */
@customElement('idb-search-results-card')
export class IdbSearchResultsCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The type of the image, video-thumbnail, or profile image.
   */
  @property({ type: String, attribute: 'image-type' })
  imageType: ImageTypes = null;

  /**
   * The color to apply to the back of the card.
   */
  @property({ type: String, attribute: 'card-background' })
  cardBackground: AllowedColors;

  @property({ type: Boolean, attribute: 'has-placeholder' })
  hasPlaceholder = false;

  /**
   * The url for the card. (This may change during refactor)
   */
  @property({ type: String, reflect: true, attribute: 'url' })
  url: string;

  /**
   * The target of the url
   */
  @property({ type: String, reflect: true, attribute: 'link-target' })
  linkTarget: LinkTargetType;

  @state() hasImageTagsSlot: boolean;
  @state() hasImageSlot: boolean;
  @state() hasCtaSlot: boolean;

  connectedCallback(): void {
    super.connectedCallback();
    this.hasImageTagsSlot = this.querySelector('[slot="image-tags"]') !== null;
    this.hasImageSlot = this.querySelector('[slot="image"]') !== null;
    this.hasCtaSlot = this.querySelector('[slot="cta"]') !== null;
  }

  _profileTypeTemplate(): TemplateResult {
    if (this.hasImageSlot) {
      return html`
        <div class="image image-profile">
          <slot name="image"></slot>
        </div>
      `;
    } else {
      return html`
        <div class="image image-profile-placeholder">
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="208" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-user"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
        </div>
      `;
    }
  }

  _videoThumbnailTypeTemplate(): TemplateResult | null {
    if (this.hasImageSlot) {
      return html`
      <div class="image image-video-thumbnail">
        <idb-icon icon="play-circle" stroke-width="2"></idb-icon>
        <span class="overlay"></span>
        <slot name="image"></slot>
      </div>
    `;
    } else return null;
  }

  _mediaTemplateSelection = () => {
    if (this.imageType === 'profile') {
      this.hasPlaceholder = true;
      return this._profileTypeTemplate();
    } else if (this.imageType === 'video-thumbnail') {
      return this._videoThumbnailTypeTemplate();
    } else if (this.hasImageSlot) {
      return html`
        <div class="image image-default">
          <slot name="image"></slot>
        </div>
      `;
    } else return null;
  };

  ctaTemplate(): TemplateResult | null {
    if (!this.url || !this.hasCtaSlot) {
      return null;
    } else {
      this.linkTarget = getUrlTarget(this.url, this.linkTarget);
      return html` <div class="card-cta">
      <idb-button button-variant="secondary" button-url="${this.url}" button-target="${this.linkTarget}">
        <slot name="cta"></slot>
      </idb-button>
    </div>`;
    }
  }

  render(): TemplateResult {
    return html`
      <section class="search-results-card">
        ${this._mediaTemplateSelection()}
        <div class="card-caption">
          <slot name="caption"></slot>
        </div>
        ${
          this.hasImageTagsSlot
            ? html`          
          <div class="image-tags ${
            this.hasImageSlot || this.hasPlaceholder
              ? 'image-tags-image'
              : 'image-tags-no-image'
          }"> 
            <slot name='image-tags'></slot>
          </div>
        `
            : ''
        }
        <div class="card-eyebrow">
          <slot name="eyebrow"></slot>
        </div>
        <div class="card-title">
          <slot name="title"></slot>
        </div>
        <div class="card-summary">
          <slot name="summary"></slot>
        </div>
        <div class="card-content">
          <slot name="content"></slot>
        </div>
        ${this.ctaTemplate()}
      </section>
    `;
  }
}
